import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Markup, { bodyClass } from "./stationmarkup";
import { usePage } from "../hooks/usePage";

import {
  getStations,
  getStationById,
  createStation,
  updateStation,
} from "../store/slices/station";

import { getSubDivisions } from "../store/slices/subdivision";
import { getCircles } from "../store/slices/circle";

const PageMarkup = memo(Markup);

export default function Stations() {
  usePage({
    title: "All Police Stations - Nellore Police Dashboard",
    bodyClass,
  });

  const dispatch = useDispatch();

  const {
    stations = [],
    pagination,
    loading,
    error,
    detailsLoading,
    createLoading,
    updateLoading,
  } = useSelector((state) => state.station);

  const {
    subdivisions = [],
    loading: subdivisionsLoading,
  } = useSelector((state) => state.subdivision);

  const {
    circles = [],
    loading: circlesLoading,
  } = useSelector((state) => state.circle);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");

  const [showModal, setShowModal] = useState(false);
  const [editingStation, setEditingStation] = useState(null);

  const [form, setForm] = useState({
    stationId: "",
    code: "",
    name: "",
    contactNumber: "",
    subDivisionId: "",
    circleId: "",
    lat: "",
    lng: "",
    active: true,
  });

  const [formError, setFormError] = useState("");

  const total = pagination?.total ?? stations.length;

  // Initial data
  useEffect(() => {
    dispatch(getStations({ page: 1, limit: 1000 }));
    dispatch(getSubDivisions({ page: 1, limit: 1000 }));
    dispatch(getCircles({ page: 1, limit: 1000 }));
  }, [dispatch]);

  // Search and status filtering
  const filteredStations = useMemo(() => {
    const searchText = search.trim().toLowerCase();

    return stations.filter((station) => {
      const searchableValues = [
        station.name,
        station.stationId,
        station.code,
        station.contactNumber,
        station.circleId?.name,
        station.circleId?.circleId,
        station.subDivisionId?.name,
        station.subDivisionId?.subDivisionId,
        station.shoOfficerId?.displayName,
        station.shoOfficerId?.username,
      ];

      const matchesSearch =
        !searchText ||
        searchableValues.some((value) =>
          String(value ?? "").toLowerCase().includes(searchText)
        );

      const matchesStatus =
        status === "ALL" ||
        (status === "ACTIVE" && station.active === true) ||
        (status === "INACTIVE" && station.active === false);

      return matchesSearch && matchesStatus;
    });
  }, [stations, search, status]);

  // Statistics
  const statistics = useMemo(
    () => ({
      total,
      active: stations.filter((s) => s.active === true).length,
      inactive: stations.filter((s) => s.active === false).length,
    }),
    [stations, total]
  );

  const refreshStations = useCallback(() => {
    return dispatch(getStations({ page: 1, limit: 1000 }));
  }, [dispatch]);

  // Reset form
  const resetForm = () => {
    setForm({
      stationId: "",
      code: "",
      name: "",
      contactNumber: "",
      subDivisionId: "",
      circleId: "",
      lat: "",
      lng: "",
      active: true,
    });

    setEditingStation(null);
    setFormError("");
  };

  // Open Add Modal
  const handleAddStation = () => {
    resetForm();
    setShowModal(true);
  };

  // Fetch station details for editing
  const handleGetStation = async (id) => {
    try {
      const response = await dispatch(getStationById(id)).unwrap();

      const station =
        response?.station ||
        response?.data?.station ||
        response?.data ||
        response;

      return station;
    } catch (error) {
      console.error("Get station error:", error);
      throw error;
    }
  };

  // Open Edit Modal
  const handleEditStation = async (station) => {
    try {
      resetForm();
      setEditingStation(station._id);
      setShowModal(true);

      const data = await handleGetStation(station._id);

      const subdivisionId =
        typeof station.subDivisionId === "object"
          ? station.subDivisionId?._id || ""
          : station.subDivisionId || "";

      const circleId =
        typeof station.circleId === "object"
          ? station.circleId?._id || ""
          : station.circleId || "";

      setForm({
        stationId: station.stationId || "",
        code: station.code || "",
        name: station.name || "",
        contactNumber: station.contactNumber || "",
        subDivisionId: subdivisionId,
        circleId: circleId,
        lat: station.lat ?? "",
        lng: station.lng ?? "",
        active: station.active ?? true,
      });
    } catch (error) {
      setFormError(
        error?.message || "Unable to load police station details"
      );
    }
  };

  // Handle form input
  const handleFormChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    setFormError("");
  };

  // Change subdivision and reset circle
  const handleSubdivisionChange = (value) => {
    setForm((prev) => ({
      ...prev,
      subDivisionId: value,
      circleId: "",
    }));
  };

  // Submit Add / Edit
  const handleSubmit = async () => {
    setFormError("");

    if (
      !form.stationId.trim() ||
      !form.code.trim() ||
      !form.name.trim() ||
      !form.contactNumber.trim() ||
      !form.subDivisionId ||
      !form.circleId
    ) {
      setFormError("Please fill all required fields.");
      return;
    }

    if (!/^[0-9]{10}$/.test(form.contactNumber.trim())) {
      setFormError("Enter a valid 10-digit contact number.");
      return;
    }

    if (form.lat === "" || form.lng === "") {
      setFormError("Latitude and longitude are required.");
      return;
    }

    const latitude = Number(form.lat);
    const longitude = Number(form.lng);

    if (
      !Number.isFinite(latitude) ||
      latitude < -90 ||
      latitude > 90
    ) {
      setFormError("Enter a valid latitude between -90 and 90.");
      return;
    }

    if (
      !Number.isFinite(longitude) ||
      longitude < -180 ||
      longitude > 180
    ) {
      setFormError("Enter a valid longitude between -180 and 180.");
      return;
    }

    const payload = {
      stationId: form.stationId.trim().toUpperCase(),
      code: form.code.trim().toUpperCase(),
      name: form.name.trim(),
      contactNumber: form.contactNumber.trim(),
      subDivisionId: form.subDivisionId,
      circleId: form.circleId,
      lat: latitude,
      lng: longitude,
      active: form.active,
    };

    try {
      if (editingStation) {
        await dispatch(
          updateStation({
            id: editingStation,
            payload,
          })
        ).unwrap();
      } else {
        await dispatch(createStation(payload)).unwrap();
      }

      setShowModal(false);
      resetForm();

      await refreshStations();
    } catch (error) {
      console.error("Save station error:", error);

      setFormError(
        error?.message ||
        error?.data?.message ||
        error ||
        "Unable to save police station"
      );
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  return (
    <PageMarkup
      stations={filteredStations}
      totalStations={total}
      statistics={statistics}
      loading={loading}
      error={error}
      search={search}
      setSearch={setSearch}
      status={status}
      setStatus={setStatus}

      // Modal
      showModal={showModal}
      editingStation={editingStation}
      form={form}
      formError={formError}
      saving={createLoading || updateLoading}
      detailsLoading={detailsLoading}

      // Dropdown data
      subdivisions={subdivisions}
      circles={circles}
      subdivisionsLoading={subdivisionsLoading}
      circlesLoading={circlesLoading}

      // Callbacks
      onAddStation={handleAddStation}
      onEditStation={handleEditStation}
      onCloseModal={handleCloseModal}
      onFormChange={handleFormChange}
      onSubdivisionChange={handleSubdivisionChange}
      onSubmit={handleSubmit}
    />
  );
}